/**
 * Estimate LRLC — penggolongan LRLC yang ditiru dengan machine learning.
 *
 * Penggolong di layar LRLC menuntut RT, VSH dan PHIE, dan Rw yang benar. Model
 * yang belajar dari sumur yang sudah digolongkan bisa memberi kelas kepada
 * sumur lain di lapangan yang sama, dan — yang lebih penting — mengatakan
 * seberapa sering ia benar di sumur yang **tidak** pernah dilihatnya. Angka itu
 * satu-satunya yang berarti di sini, jadi yang dipajang adalah rapor sumur
 * buta per kelas, bukan akurasi latihan.
 *
 * Kolam latihnya dipilih per lapangan: satu struktur, beberapa, atau seluruh
 * proyek. Seluruh Prabumulih adalah 606 sumur dan 2,4 juta sampel, dan itu
 * tidak muat di satu permintaan: pelatihan dan penerapan berjalan sebagai
 * pekerjaan latar, dan jendela ini menanyakan keadaannya sampai selesai.
 *
 * Diuji pada 27 September 2026 atas LRLC_CLASS Prabumulih, jendela ±4:
 * XGBoost 0,963 dan Random Forest 0,961 akurasi buta, ANN 0,941, SVM 0,924.
 * Tingginya karena kelasnya sendiri dihitung dari RT, VSH dan PHIE — model
 * ini meniru penggolong, dan itu yang dikatakan catatannya kepada pemakai.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api";
import { useAsync } from "../hooks";
import { count } from "../lib/plural";
import type { LearnerMethod, LrlcClass } from "../types";
import { Button, Checkbox, Dropdown, NumberField, Notice, Panel, Segmented,
  Toggle, useToast } from "./ui";
import { CompareMethods, EstimateWells, ModelTable, PerFieldView, Swatch, fieldOfModel, pct,
  type LrlcModel } from "./LrlcEstimateViews";
import EstimateLog from "./LrlcEstimateLog";
import BlindSheet from "./LrlcBlindSheet";

/** Masukan yang diuji; yang tidak ada di proyek ini tidak dicentang. */
export const DEFAULT_INPUTS = ["GR", "RHOB", "NPHI", "RT", "VSH", "PHIE", "MD"];

/** Keempat metode yang diuji, dalam urutan hasilnya. */
export const LRLC_METHODS: { key: string; label: string }[] = [
  { key: "xgboost", label: "XGBoost" },
  { key: "random_forest", label: "Random forest" },
  { key: "ann", label: "ANN" },
  { key: "svm", label: "SVM" },
];

export type Scope = "one" | "several" | "all";

/** Nama lapangan yang dipajang untuk sumur yang lapangannya tidak tercatat. */
export const NO_FIELD = "(no field)";

export interface EstimateWell {
  id: number;
  name: string;
  field?: string;
}

/** Sumur yang masuk kolam, menurut lingkup dan lapangan yang dipilih. */
export function poolOf(wells: EstimateWell[], scope: Scope, fields: Set<string>): number[] {
  if (scope === "all") return wells.map((w) => w.id);
  return wells.filter((w) => fields.has(w.field || NO_FIELD)).map((w) => w.id);
}

interface Job {
  id: number;
  kind: "train" | "apply";
  status: "running" | "done" | "failed";
  stage: string;
  error: string;
  result: {
    results?: { well: string }[];
    /** Sumur yang dilewati penerapan, atau lapangan yang dilewati pelatihan. */
    skipped?: { well?: string; field?: string; reason: string }[];
    /** Pelatihan per lapangan: model yang lahir, satu tiap lapangan. */
    models?: number[];
    fields?: { field: string; model: number; accuracy: number | null }[];
  };
  model_id: number | null;
  model?: LrlcModel;
}

export default function LrlcEstimate({
  projectId,
  wells,
  available,
  classes,
  mayCompute,
  onClose,
}: {
  projectId: number;
  /** Sumur yang sudah dibor, berikut lapangannya. */
  wells: EstimateWell[];
  /** Kurva yang ada di proyek ini. */
  available: string[];
  classes: LrlcClass[];
  mayCompute: boolean;
  onClose: () => void;
}) {
  const toast = useToast();

  useEffect(() => {
    const escape = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    document.addEventListener("keydown", escape);
    return () => document.removeEventListener("keydown", escape);
  }, [onClose]);

  const fieldNames = useMemo(() => {
    const tally = new Map<string, number>();
    for (const well of wells) {
      const name = well.field || NO_FIELD;
      tally.set(name, (tally.get(name) ?? 0) + 1);
    }
    return [...tally.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [wells]);

  const [scope, setScope] = useState<Scope>(fieldNames.length > 1 ? "one" : "all");
  // Lapangan terbesar lebih dulu: yang pertama menurut abjad bisa saja satu
  // sumur, dan satu sumur tidak bisa sekaligus dilatih dan diuji.
  const [one, setOne] = useState(
    () => [...fieldNames].sort((a, b) => b[1] - a[1])[0]?.[0] ?? "");
  const [several, setSeveral] = useState<Set<string>>(new Set());
  const fields = scope === "one" ? new Set([one]) : several;
  const pool = useMemo(() => poolOf(wells, scope, fields),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [wells, scope, one, several]);

  /* Masukan yang diuji di atas, dalam urutannya; sisanya menurut abjad. Di
     Prabumulih proyeknya membawa ratusan kurva, dan ketujuh yang tercentang
     tenggelam di antara AHT10 dan BVW. */
  const offered = useMemo(() => {
    const all = [...new Set([...available.map((c) => c.toUpperCase()), "MD"])];
    const first = DEFAULT_INPUTS.filter((c) => all.includes(c));
    return [...first, ...all.filter((c) => !first.includes(c)).sort()];
  }, [available]);
  const [inputs, setInputs] = useState<Set<string>>(
    () => new Set(DEFAULT_INPUTS.filter(
      (c) => c === "MD" || available.some((name) => name.toUpperCase() === c))));
  const [kind, setKind] = useState("xgboost");
  const [half, setHalf] = useState(4);
  const [allRows, setAllRows] = useState(false);
  /** Satu model tiap lapangan, alih-alih satu model untuk seluruh kolam. */
  const [perField, setPerField] = useState(false);
  const splitting = perField && scope !== "one";
  const poolFields = useMemo(() => {
    const inPool = new Set(pool);
    return new Set(wells.filter((w) => inPool.has(w.id) && w.field).map((w) => w.field!));
  }, [wells, pool]);

  const methods = useAsync(
    (signal) => api.get<{ methods: LearnerMethod[] }>("/api/ml-methods/", {}, signal), []);
  const method = methods.data?.methods?.find((m) => m.key === kind) ?? null;

  const saved = useAsync(
    (signal) => api.get<{ results: LrlcModel[] }>(
      "/api/log-models/", { project: projectId }, signal),
    [projectId]);
  /** Model yang barusan dilatih, dipakai langsung tanpa menunggu daftarnya dimuat ulang. */
  const [trained, setTrained] = useState<LrlcModel | null>(null);
  const lrlcModels = useMemo(() => {
    const listed = (saved.data?.results ?? []).filter((m) => m.target === "LRLC_CLASS");
    return trained && !listed.some((m) => m.id === trained.id) ? [trained, ...listed] : listed;
  }, [saved.data, trained]);
  const [modelId, setModelId] = useState<number | null>(null);
  /* Tanpa pilihan, yang dipakai adalah yang skor butanya tertinggi. */
  const bestId = useMemo(() => [...lrlcModels].sort(
    (a, b) => (b.blind_score.value ?? 0) - (a.blind_score.value ?? 0))[0]?.id ?? null,
  [lrlcModels]);
  const model = lrlcModels.find((m) => m.id === (modelId ?? bestId)) ?? null;
  /** Model yang dibandingkan. Tanpa pilihan: yang terbaru dari tiap metode. */
  const [compareIds, setCompareIds] = useState<Set<number> | null>(null);
  const compared = useMemo(() => {
    if (compareIds) return compareIds;
    const latest = new Map<string, number>();
    for (const m of lrlcModels) if (!latest.has(m.kind)) latest.set(m.kind, m.id);
    return new Set([...latest.values()].slice(0, 6));
  }, [compareIds, lrlcModels]);
  const [tab, setTab] = useState<"models" | "fields" | "estimate" | "compare" | "log">("models");
  /** Pembanding model lapangan: model yang dipakai bila ia gabungan, atau
   *  model gabungan terbaik. */
  const overall = useMemo(() => {
    if (model && !fieldOfModel(model)) return model;
    return [...lrlcModels].filter((m) => !fieldOfModel(m))
      .sort((a, b) => (b.blind_score.value ?? 0) - (a.blind_score.value ?? 0))[0] ?? null;
  }, [model, lrlcModels]);

  const [job, setJob] = useState<Job | null>(null);
  /** Dari klik sampai server menjawab: tombolnya sudah mati, supaya klik
   *  kedua tidak memulai pekerjaan kedua. */
  const [starting, setStarting] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  /** Palsu sesudah jendela ditutup: poll yang masih di jalan berhenti di sana. */
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; window.clearTimeout(timer.current); };
  }, []);

  /** Tanyakan keadaan pekerjaan sampai ia selesai atau gagal. */
  const follow = (started: Job) => {
    setJob(started);
    let misses = 0;
    const poll = async () => {
      if (!alive.current) return;
      let now: Job;
      try {
        now = await api.get<Job>(`/api/log-models/jobs/${started.id}/`);
        misses = 0;
      } catch (cause) {
        // Satu jawaban yang hilang — nginx dimuat ulang, jaringan tersendat —
        // bukan akhir pekerjaannya; ia terus berjalan di server.
        if (!alive.current) return;
        misses += 1;
        if (misses < 6) {
          timer.current = window.setTimeout(poll, 2000 * misses);
          return;
        }
        toast(`Lost touch with the job: ${cause instanceof Error ? cause.message : String(cause)}`,
              "error");
        return;
      }
      if (!alive.current) return;
      setJob(now);
      if (now.status === "running") {
        timer.current = window.setTimeout(poll, 2000);
        return;
      }
      if (now.status === "failed") {
        toast(now.error, "error");
      } else if (now.kind === "train" && now.result.models) {
        saved.reload();
        setTab("fields");
        const skipped = now.result.skipped?.length ?? 0;
        toast(`Trained ${count(now.result.models.length, "field model")}`
              + (skipped ? `; ${count(skipped, "field")} left out.` : "."));
      } else if (now.kind === "train" && now.model) {
        setTrained(now.model as LrlcModel);
        setModelId(now.model.id);
        setTab("models");
        saved.reload();
        toast(`Trained: blind accuracy ${pct(now.model.blind_score.value)}.`);
      } else if (now.kind === "apply") {
        const done = now.result.results?.length ?? 0;
        // Yang baru saja ditulis langsung bisa dilihat, berdampingan dengan
        // taksiran sebelumnya.
        setTab("log");
        toast(`LRLC_CLASS_EST written to ${count(done, "well")}.`);
      }
    };
    timer.current = window.setTimeout(poll, 1000);
  };

  /* Jendela yang dibuka ulang mengikuti lagi pekerjaan proyek ini yang masih
     berjalan, alih-alih menawarkan tombol yang akan memulai yang kedua. */
  useEffect(() => {
    let cancelled = false;
    api.get<{ running: Job | null }>("/api/log-models/jobs/", { project: projectId })
      .then((body) => { if (!cancelled && body?.running) follow(body.running); })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const running = starting || job?.status === "running";

  /** Mulai satu pekerjaan; kegagalan memulainya — termasuk 409 — dikabarkan. */
  const start = async (send: () => Promise<Job>) => {
    setStarting(true);
    try {
      follow(await send());
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "error");
    } finally {
      setStarting(false);
    }
  };

  const train = () => {
    const label = LRLC_METHODS.find((m) => m.key === kind)?.label ?? kind;
    const where = scope === "all" ? "all fields" : [...fields].join(", ");
    return start(() => api.post<Job>("/api/log-models/", {
      project: projectId,
      target: "LRLC_CLASS",
      inputs: [...inputs],
      kind,
      half_window: half,
      wells: pool,
      background: true,
      ...(allRows ? { all_rows: true } : {}),
      // Per lapangan: server menambahkan nama lapangannya pada tiap model.
      ...(splitting ? { per_field: true, name: `LRLC ${label} ±${half}` }
        : { name: `LRLC ${label} ±${half} — ${where}`.slice(0, 120),
            ...(scope === "one" ? { field: one } : {}) }),
    }));
  };

  const applyByField = (ids: number[], fieldModels: number[], fallback: number | null) =>
    start(() => api.post<Job>("/api/log-models/apply-by-field/", {
      project: projectId, wells: ids, models: fieldModels,
      ...(fallback ? { fallback } : {}) }));

  const apply = (ids: number[]) => {
    if (!model || !ids.length) return;
    return start(() => api.post<Job>(`/api/log-models/${model.id}/apply/`,
      { wells: ids, background: true }));
  };

  const colorOf = (code: number) => classes.find((c) => c.code === code)?.color ?? "var(--ink-6)";
  const labelOf = (code: number) => classes.find((c) => c.code === code)?.label ?? String(code);

  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 110, background: "var(--app-bg)",
               display: "flex", flexDirection: "column" }}
      role="dialog"
      aria-modal="true"
      aria-label="Estimate LRLC"
    >
      <header style={{ display: "flex", alignItems: "center", gap: 9, flex: "none",
                       padding: "7px 11px", background: "var(--toolbar)",
                       borderBottom: "1px solid var(--line-strong)" }}>
        <span style={{ font: "600 12px var(--font-sans)", color: "var(--ink)" }}>
          Estimate LRLC
        </span>
        <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-6)" }}>
          machine learning on LRLC_CLASS · scored on blind wells
        </span>
        <span style={{ flex: 1 }} />
        <Button variant="ghost" onClick={onClose}>Close</Button>
      </header>

      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        <div className="scroll-y" style={{ width: 300, flex: "none", padding: 10,
                                           display: "flex", flexDirection: "column", gap: 9,
                                           borderRight: "1px solid var(--line)" }}>
          <Panel title="Wells to learn from" meta={count(pool.length, "well")}>
            <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
              <Segmented
                value={scope}
                options={[
                  { value: "one", label: "One field" },
                  { value: "several", label: "Several" },
                  { value: "all", label: "All fields" },
                ]}
                onChange={(value) => setScope(value as Scope)}
                size="sm"
              />
              {scope === "one" && (
                <Dropdown
                  label="Field"
                  value={one}
                  options={fieldNames.map(([name, n]) => ({ value: name, label: `${name} (${n})` }))}
                  onChange={setOne}
                />
              )}
              {scope === "several" && (
                <div style={{ display: "flex", flexDirection: "column", maxHeight: 180,
                              overflowY: "auto" }}>
                  {fieldNames.map(([name, n]) => (
                    <Checkbox
                      key={name}
                      checked={several.has(name)}
                      label={name}
                      meta={String(n)}
                      onChange={(on) => {
                        const next = new Set(several);
                        on ? next.add(name) : next.delete(name);
                        setSeveral(next);
                      }}
                    />
                  ))}
                </div>
              )}
              {scope !== "one" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <span style={{ font: "500 10px var(--font-sans)", color: "var(--ink-4)" }}>
                    Models
                  </span>
                  <Segmented
                    value={perField ? "field" : "all"}
                    options={[
                      { value: "all", label: "One for all" },
                      { value: "field", label: `One per field (${poolFields.size})` },
                    ]}
                    onChange={(value) => setPerField(value === "field")}
                    size="sm"
                  />
                </div>
              )}
              <span style={{ font: "400 9.5px/1.5 var(--font-sans)", color: "var(--ink-6)" }}>
                {splitting
                  ? `One model is trained for each of the ${poolFields.size} fields, `
                    + "from that field's wells only; a field with fewer than two "
                    + "classified wells is left out. "
                  : ""}
                Only wells that already carry LRLC_CLASS teach the model — run
                "Flag the low-resistivity pay" and publish first. A quarter of
                them are held back as blind wells and never seen in training.
                Which wells receive the estimate is chosen in the Estimate
                wells tab.
              </span>
            </div>
          </Panel>

          <Panel title="Input curves" meta={`${inputs.size} chosen`}>
            <div style={{ display: "flex", flexDirection: "column", maxHeight: 200,
                          overflowY: "auto" }}>
              {offered.map((name) => (
                <Checkbox
                  key={name}
                  checked={inputs.has(name)}
                  label={name}
                  meta={name === "MD" ? "depth" : undefined}
                  onChange={(on) => {
                    const next = new Set(inputs);
                    on ? next.add(name) : next.delete(name);
                    setInputs(next);
                  }}
                />
              ))}
            </div>
          </Panel>

          <Panel title="Method">
            <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
              <Segmented
                value={kind}
                options={LRLC_METHODS.map((m) => ({ value: m.key, label: m.label }))}
                onChange={setKind}
                size="sm"
              />
              {method && (
                <span style={{ font: "400 9.5px/1.5 var(--font-sans)", color: "var(--ink-6)" }}>
                  {method.note}
                  {method.row_cap && !allRows
                    ? ` Training rows capped at ${method.row_cap.toLocaleString()}.` : ""}
                </span>
              )}
              <NumberField label="Depth window ± samples" value={half} step={1}
                           onChange={(value) => setHalf(Math.max(0, Math.min(10, Math.round(value))))} />
              {method?.row_cap ? (
                <Toggle label="Train on every row (no cap)" checked={allRows}
                        onChange={setAllRows} />
              ) : null}
              {allRows && kind === "svm" && (
                <span style={{ font: "400 9.5px/1.5 var(--font-sans)", color: "var(--warn-ink)" }}>
                  SVM grows with the square of the row count. On a whole
                  field this runs for hours.
                </span>
              )}
              <Button
                variant="primary"
                onClick={train}
                busy={running && job?.kind === "train"}
                disabled={!mayCompute || running || pool.length < 2 || inputs.size === 0
                          || (splitting && poolFields.size === 0)
                          || (method !== null && !method.available)}
                title={mayCompute ? undefined : "Your role on this project may not train models"}
              >
                {splitting ? `Train ${count(poolFields.size, "field model")}`
                  : "Train and test on blind wells"}
              </Button>
            </div>
          </Panel>
        </div>

        <div className="scroll-y" style={{ flex: 1, minWidth: 0, padding: 10,
                                           display: "flex", flexDirection: "column", gap: 9 }}>
          <Segmented
            value={tab}
            options={[
              { value: "models", label: `Models (${lrlcModels.length})` },
              { value: "fields", label: "Per field" },
              { value: "estimate", label: "Estimate wells" },
              { value: "compare", label: "Compare methods" },
              { value: "log", label: "Log display" },
            ]}
            onChange={(value) => setTab(value as typeof tab)}
            size="sm"
          />

          {job && job.status === "running" && (
            <Notice tone="info">{job.stage || "Working"}…</Notice>
          )}
          {job && job.status === "failed" && <Notice tone="error">{job.error}</Notice>}
          {job && job.status === "done" && job.kind === "apply" && (
            <Notice tone="info">
              LRLC_CLASS_EST written to {count(job.result.results?.length ?? 0, "well")}
              {job.result.skipped?.length
                ? `; ${count(job.result.skipped.length, "well")} left out, mostly for missing input curves`
                : ""}. It is an estimate, not a classification: it sits in the
              ESTIMATED set, apart from LRLC_CLASS.
            </Notice>
          )}

          {tab === "models" && (
            <>
              <ModelTable models={lrlcModels} selected={model?.id ?? null}
                          onSelect={setModelId} compared={compared}
                          onCompare={setCompareIds} />
              {model && <ModelReport model={model} colorOf={colorOf} labelOf={labelOf} />}
            </>
          )}
          {tab === "estimate" && (
            <EstimateWells model={model} models={lrlcModels} wells={wells} running={running}
                           mayCompute={mayCompute} onEstimate={apply}
                           onEstimateByField={applyByField} />
          )}
          {tab === "log" && <EstimateLog projectId={projectId} classes={classes} />}
          {tab === "fields" && (
            <PerFieldView models={lrlcModels} wells={wells} overall={overall} />
          )}
          {tab === "compare" && (
            <CompareMethods projectId={projectId} wells={wells} models={lrlcModels}
                            compared={compared} classes={classes}
                            suggested={model?.wells_blind ?? []} />
          )}
        </div>
      </div>
    </div>
  );
}

const cell: React.CSSProperties = {
  padding: "4px 8px", textAlign: "right", font: "400 10.5px var(--font-mono)",
  borderBottom: "1px solid var(--line-soft)", whiteSpace: "nowrap",
};
const head: React.CSSProperties = {
  ...cell, font: "600 9.5px var(--font-mono)", color: "var(--ink-5)",
  borderBottom: "1px solid var(--line-strong)", background: "var(--panel)",
};
const left: React.CSSProperties = { textAlign: "left" };

function ModelReport({ model, colorOf, labelOf }: {
  model: LrlcModel;
  colorOf: (code: number) => string;
  labelOf: (code: number) => string;
}) {
  const report = model.metrics.blind_classes;
  const blind = model.metrics.per_well.filter((row) => row.blind);
  const usage = model.metrics.usage;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
      <div style={{ display: "flex", gap: 18, flexWrap: "wrap",
                    font: "400 10.5px var(--font-mono)", color: "var(--ink-4)" }}>
        <span>Blind accuracy <b style={{ color: "var(--accent)", fontSize: 14 }}>
          {pct(model.blind_score.value)}</b></span>
        <span>Macro F1 <b style={{ color: "var(--ink-2)", fontSize: 14 }}>
          {pct(model.blind_score.extra)}</b></span>
        <span>{model.kind_label} · ±{model.half_window}</span>
        <span>{count(model.wells_trained.length, "well")} trained,{" "}
          {model.wells_blind.length} blind</span>
        {usage?.rows_used ? (
          <span>{usage.rows_used.toLocaleString("en-US")} of{" "}
            {(usage.rows_available ?? usage.rows_used).toLocaleString("en-US")} rows</span>
        ) : null}
        <span>Inputs {model.inputs.join(", ")}</span>
      </div>

      {report && (
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "flex-start" }}>
          <table style={{ borderCollapse: "collapse", background: "var(--panel-inset)",
                          border: "1px solid var(--line)" }}>
            <thead>
              <tr>
                <th style={{ ...head, ...left }}>Class, on blind wells</th>
                <th style={head}>Samples</th>
                <th style={head}>Predicted</th>
                <th style={head}>Precision</th>
                <th style={head}>Recall</th>
                <th style={head}>F1</th>
              </tr>
            </thead>
            <tbody>
              {report.rows.map((row) => (
                <tr key={row.code}>
                  <td style={{ ...cell, ...left, color: "var(--ink-2)" }}>
                    <Swatch color={colorOf(row.code)} label={labelOf(row.code)} />
                  </td>
                  <td style={cell}>{row.n.toLocaleString("en-US")}</td>
                  <td style={cell}>{row.predicted.toLocaleString("en-US")}</td>
                  <td style={cell}>{pct(row.precision)}</td>
                  <td style={cell}>{pct(row.recall)}</td>
                  <td style={{ ...cell, color: "var(--accent)" }}>{pct(row.f1)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <table style={{ borderCollapse: "collapse", background: "var(--panel-inset)",
                          border: "1px solid var(--line)" }}
                 aria-label="Confusion matrix">
            <thead>
              <tr>
                <th style={{ ...head, ...left }}>True ↓ · predicted →</th>
                {report.labels.map((code) => (
                  <th key={code} style={head} title={labelOf(code)}>{code}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {report.labels.map((code, i) => {
                const total = report.confusion[i].reduce((a, b) => a + b, 0) || 1;
                return (
                  <tr key={code}>
                    <td style={{ ...cell, ...left }}>
                      <Swatch color={colorOf(code)} label={labelOf(code)} />
                    </td>
                    {report.confusion[i].map((n, j) => (
                      <td key={j} style={{
                        ...cell,
                        background: i === j
                          ? `color-mix(in srgb, var(--accent) ${Math.round((n / total) * 45)}%, transparent)`
                          : undefined,
                      }}>
                        {n.toLocaleString("en-US")}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Sumur buta digambar: kelas asli di sebelah kelas ML. Dipasang di
          rapor model, jadi ia tampil begitu pelatihan selesai dan setiap kali
          sebuah model dipilih di tabelnya. */}
      {blind.length > 0 && model.task === "classification" && (
        <BlindSheet key={model.id} modelId={model.id} />
      )}

      {blind.length > 0 && (
        <details>
          <summary style={{ font: "500 10.5px var(--font-sans)", color: "var(--ink-3)",
                            cursor: "pointer" }}>
            Blind wells, one by one ({blind.length})
          </summary>
          <table style={{ borderCollapse: "collapse", marginTop: 6 }}>
            <thead>
              <tr>
                <th style={{ ...head, ...left }}>Well</th>
                <th style={head}>Samples</th>
                <th style={head}>Accuracy</th>
                <th style={head}>Macro F1</th>
              </tr>
            </thead>
            <tbody>
              {[...blind].sort((a, b) => (a.accuracy ?? 0) - (b.accuracy ?? 0)).map((row) => (
                <tr key={row.well}>
                  <td style={{ ...cell, ...left }}>{row.well}</td>
                  <td style={cell}>{row.n.toLocaleString("en-US")}</td>
                  <td style={cell}>{pct(row.accuracy)}</td>
                  <td style={cell}>{pct(row.f1_macro)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}

      <span style={{ font: "400 10px/1.6 var(--font-sans)", color: "var(--ink-6)", maxWidth: 760 }}>
        LRLC_CLASS is itself computed from RT, VSH and PHIE, so a model given
        those curves learns to imitate the classifier — which is why the score
        is high. Its use is on wells where the classifier cannot run or Rw is
        not known. "LRLC proven" needs a production test and cannot be
        learned from curves; classes absent from the blind wells are not in
        the table because they were not tested.
        {model.metrics.skipped.length > 0 &&
          ` ${count(model.metrics.skipped.length, "well")} of the scope were left out, most for carrying no LRLC_CLASS.`}
      </span>
    </div>
  );
}
